import drinkImage from "./Assets/Projects/beverage-dispenser.jpg";
import drinkPdf from "./Assets/Projects/beverage_table.pdf";
import robotImage from "./Assets/Projects/rolling-robot.jpg";
import bldcReport from "./Assets/Projects/BLDC_Motor_Control.pdf";
import rotorReport from "./Assets/Projects/BLDC Motor Control Project.pdf";
import petReport from "./Assets/Projects/WatchDogReport.pdf";
import resumePdf from "./Assets/Max_Kotas_Resume.pdf";

export const resumeFile = resumePdf;

export const profiles = {
  email: "mailto:max@maxkotas.com",
  emailLabel: "max@maxkotas.com",
  linkedin: "https://www.linkedin.com/in/max-kotas",
  github: "https://github.com/maxkotas",
};

export const summary = {
  lead:
    "Designing battery management system boards, primarily for data centers, energy storage systems, and industrial and medical devices.",
  support: "Multilayer mixed-signal PCBA design, validation, and production support.",
};

export const roleLine =
  "Associate Electrical Engineer, Smart Battery Systems, Panasonic Energy, The Colony, TX, 2025–Present.";

export const cases = [
  {
    title: "Protection validation",
    outcome: "Overcurrent, recovery, and short-circuit behavior measured on the bench.",
    body: [
      "Electrical validation and protection testing across multiple development programs covered overcharge protection at two levels, over-discharge protection, and consumption current on engineering PCA samples. Formal reports were written and filed in the controlled engineering directories.",
      "Discharge-overcurrent, recovery, and short-circuit testing in the Texas lab measured about an 8.3 A threshold, about 7.8 s to detect, and about 17.1 s to recover against a requirement greater than 16 s. Short-circuit detection was within 410 μs. Those results went into the formal verification report.",
      "SMBus logs and thermal images traced a pack-overheating case to MOSFET RDS(on) and limited shared-drain copper. Lower-RDS(on) parts passed the next thermal test. An intermittent high DET-to-GND resistance in the field traced to flex tears at a Nicomatic Crimpflex connection.",
    ],
  },
  {
    title: "Production inspection",
    outcome: "Protection-PCA changes carried into the pack-inspection specification.",
    body: [
      "Redevelopment support covered platforms whose protection PCA changed: a protection IC and FET replacement, a fuse added for IEC 62133-2:2017, the layout change from that fuse, and a PCB manufacturer change. Electrical protection evaluations were run on the changed designs.",
      "The same changes went into production inspection: a shutdown step using an SMBus block-write shutdown command, shutdown confirmation from pack voltage and SMBus, firmware and FET reference updates, revised FET-resistance criteria, and an output-impedance limit moved from 100 mΩ to 110 mΩ.",
      "The revised pack-inspection specification was uploaded to the controlled document system when the shutdown-command revision was missing.",
    ],
  },
  {
    title: "Alternate components",
    outcome: "Electrically acceptable substitutes separated from open footprint and margin questions.",
    body: [
      "For supply continuity and end-of-life substitutions, candidate MOSFETs were compared on VDS, ID, RDS(on), and total gate charge. Parts that were electrically acceptable were separated from footprint and voltage-margin questions that still needed confirmation.",
      "On another PCA, most proposed alternates were electrically compatible, including a capacitor series with equal or better specs. Fuse-resistor replacements whose opening characteristics did not match the original were flagged, the closer match was recommended, and fault-protection requirements were called out for review before approval.",
      "The comparisons were documented for the engineering-change decision.",
    ],
  },
  {
    title: "Supply-driven redesign",
    outcome: "A constrained FET replaced, with factory protection limits still above the application current.",
    body: [
      "A supply-constrained FET was replaced, a 5 A slow-blow fuse was added, and the protection IC threshold variant was changed, while the PCA outline and mounting area stayed the same. Certification impact was tracked with the change.",
      "Protection behavior for the factory moved with the circuit. Discharge overcurrent went from about 5.0 A / 24 ms to 4.0 A / 12 ms, charge overcurrent from about 3.33 A / 4 ms to 2.5 A / 4 ms, and short circuit from about 26.7 A / 0.4 ms to 16.7 A / 0.4 ms. The new discharge and charge limits still sat above the application needs, about 3 A and 2 A.",
      "Over-discharge detection delay dropped from 128 ms to 24 ms, so the manufacturing site added an over-discharge protection test and revised the sample quantities. The change explanation covered evaluation planning, SPI inspection, and manufacturing prep, and matched the engineering explanation prepared for the customer. FET stock on hand covered production until September 2027.",
    ],
  },
  {
    title: "Pack schematics",
    outcome: "A 3S pack schematic in Zuken, far enough along for quotation.",
    body: [
      "A new 3S pack schematic was developed in Zuken from a proven 2S architecture: a bq40z50 BMU, LEDs, a power path for 6 A continuous discharge, and a fuse for an IEC 61010 requirement. It reached about 90% for early review, then gained a second parallel power-FET pair as do-not-populate, the fuse, and the 3S fuel-gauge variant.",
      "Schematic properties identified the correct parts before they were in the Zuken library. That schematic was what quotation needed.",
      "A 2011 PCA schematic that existed only in a non-editable format was redrawn by hand, obsolete BOM parts were updated, and the drawing went out for an electrical comparison against the original so the team had an editable baseline for a transfer.",
    ],
  },
];

export const supportNotes = [
  "Medical devices in this work include infusion pumps and AEDs. Boards use protection ICs, charge and discharge MOSFETs, current sensing, thermistors, fuses, and SMBus.",
  "Cell characterization (ChemID) for a new cell: three loose cells, no pack, no external protection IC, datasheet charge limits, form submitted with the cells through the BMU supplier’s local FAE.",
  "On-site production support in Mexico.",
];

export const metrics = [
  {
    title: "20+ PCB builds",
    note: "From fabrication through mass production",
  },
  {
    title: "UL 2054",
    note: "and IEC 62133-2",
  },
  {
    title: "UN 38.3",
    note: "Transport testing",
  },
  {
    title: "IPC CID",
    note: "February 2026",
  },
];

export const selectedProjects = [
  {
    title: "Automated Beverage Dispenser",
    meta: "October 2024 · ESP32 · Custom PCB · 3D printing",
    paragraphs: [
      "Built an ESP32-controlled dispenser from a development-board proof of concept to an integrated, hand-assembled PCB. The board combines voltage regulation from a 12 V input, stepper-driven lead-screw motion, and MOSFET-switched peristaltic pumps.",
      "Modeled and iterated 3D-printed brackets for an aluminum-extrusion frame, refining mechanical rigidity and fit alongside the electrical integration.",
    ],
    image: drinkImage,
    width: 578,
    height: 1024,
    alt: "Automated beverage dispenser on a kitchen counter: white 3D-printed table, clear cup, curved tubes, and a black lead-screw column.",
    links: [{ href: drinkPdf, label: "Project PDF" }],
  },
  {
    title: "Rolling Robot Controller",
    meta: "February 2025 · KiCad · ESP32 · Motor control",
    paragraphs: [
      "Designed a controller PCB for a rolling-robot concept with an ESP32, TB6612FNG motor driver, and MPU6050 IMU, including motor power, sensor interfaces, and decoupling.",
    ],
    image: robotImage,
    width: 1024,
    height: 578,
    alt: "Round green rolling-robot controller PCB labeled KEEP-OUT ZONE, with an ESP32-style module, connectors, and a small video inset of Max.",
    links: [{ href: "https://youtu.be/bdaRhlA1MY4", label: "Watch demo" }],
  },
];

export const earlierWork = [
  {
    title: "BLDC six-step commutation",
    text: "Hall-sensor feedback, a three-phase inverter, and PI speed control.",
    href: bldcReport,
    label: "Report",
  },
  {
    title: "Rotor position sensor",
    text: "Hall-effect sensing and an H-bridge for BLDC rotor position.",
    href: rotorReport,
    label: "Report",
  },
  {
    title: "FPGA neural network",
    text: "A quantized network trained on MNIST and synthesized for an FPGA.",
    href: "https://github.com/maxkotas/fpga_accelerated_nn",
    label: "GitHub",
  },
  {
    title: "Thermal printer camera",
    text: "Camera frames processed and sent to a thermal printer over UART.",
    href: "https://github.com/maxkotas/thermal-camera-printer",
    label: "GitHub",
  },
  {
    title: "IoT pet tracker",
    text: "A GPS collar with live video, alerts, and a receiver.",
    href: petReport,
    label: "Report",
  },
  {
    title: "SEO pipeline",
    text: "A Python crawl that builds word clouds and comparison reports.",
    href: "https://github.com/maxkotas/SEOWordcloud",
    label: "GitHub",
  },
];

export const skillGroups = [
  {
    title: "Design",
    text: "Zuken, KiCad, Altium (familiarity), SPICE, schematic capture, component selection and derating",
  },
  {
    title: "Lab",
    text: "Oscilloscopes, logic analyzers, DMMs, milliohmmeters, thermal imaging, electronic loads, soldering",
  },
  {
    title: "Embedded",
    text: "ESP32, C/C++, Python, SMBus, I2C, MQTT, TI bqStudio, bq40z50-R2, bq41z90",
  },
];
